import { z } from 'zod';
import { CardTransactionSchema } from './card.schema';
import { CARD_OPERATION_STATUSES, CARD_STATUSES } from './card.types';

const EventId = z.string().nullable().optional();

const RawAmount = z
  .string()
  .regex(/^-?\d+$/)
  .transform(BigInt);

export const CardIssuedEventSchema = z.object({
  eventId: EventId,
  operationId: z.string(),
  cardId: z.string(),
  last4Digits: z.string(),
  currencyCode: z.string(),
  paymentSystem: z.string().nullable(),
  status: z.enum(CARD_STATUSES).catch('UNKNOWN' as never),
});

export const CardOperationProgressEventSchema = z.object({
  eventId: EventId,
  operationId: z.string(),
  status: z.enum(CARD_OPERATION_STATUSES).catch('UNKNOWN' as never),
});

export const CardOperationFailedEventSchema = z.object({
  eventId: EventId,
  operationId: z.string(),
  type: z.string(),
  failureReason: z.string().nullable(),
});

export const CardTopupCompletedEventSchema = z.object({
  eventId: EventId,
  operationId: z.string(),
  cardId: z.string(),
  fiatAmountMinor: RawAmount.nullable(),
  fiatCurrency: z.string().nullable(),
  currencyPrecision: z.number().int().nullable(),
  balanceMinor: RawAmount.nullable(),
});

export const CardOperationRefundedEventSchema = z.object({
  eventId: EventId,
  operationId: z.string(),
  type: z.string(),
  amountRaw: RawAmount,
  txHash: z.string().nullable(),
});

export const CardTransactionEventSchema = z.object({
  eventId: EventId,
  cardId: z.string(),
  transaction: CardTransactionSchema,
});

export const CardStatusChangedEventSchema = z.object({
  eventId: EventId,
  cardId: z.string(),
  status: z.enum(CARD_STATUSES).catch('UNKNOWN' as never),
  previousStatus: z
    .enum(CARD_STATUSES)
    .catch('UNKNOWN' as never)
    .nullable(),
});

export const CardThreeDsOtpEventSchema = z.object({
  cardId: z.string(),
  type: z.string(),
  code: z.string().nullable(),
});

export type CardEvent =
  | { name: 'card.issued'; payload: z.infer<typeof CardIssuedEventSchema> }
  | { name: 'card.operation.progress'; payload: z.infer<typeof CardOperationProgressEventSchema> }
  | { name: 'card.operation.failed'; payload: z.infer<typeof CardOperationFailedEventSchema> }
  | { name: 'card.topup.completed'; payload: z.infer<typeof CardTopupCompletedEventSchema> }
  | { name: 'card.operation.refunded'; payload: z.infer<typeof CardOperationRefundedEventSchema> }
  | { name: 'card.transaction'; payload: z.infer<typeof CardTransactionEventSchema> }
  | { name: 'card.status.changed'; payload: z.infer<typeof CardStatusChangedEventSchema> }
  | { name: 'card.3ds.otp'; payload: z.infer<typeof CardThreeDsOtpEventSchema> };
